import "@/App.css";
import "@/styles/public-site.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import CookieBanner, { getCookieConsent } from "@/components/CookieBanner";
import IntroAnimation from "@/components/IntroAnimation";
import { loadAnalytics } from "@/lib/analytics";
import { PORTAL_URL } from "@/lib/drivego";

import { usePageSeo } from "@/lib/use-page-seo";

// Pages — forsiden ligger i hovedbundlen; resten hentes først, når de besøges.
import LandingPage from "@/pages/LandingPage";
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const BookingSuccess = lazy(() => import("@/pages/BookingSuccess"));
const BookingCancel = lazy(() => import("@/pages/BookingCancel"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const BusinessPage = lazy(() => import("@/pages/BusinessPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

/** Sender gamle «Mine bestillinger»-links videre til DriveGo-kundeportalen. */
const ExternalRedirect = ({ to }) => {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
};

function AppRoutes() {
  const location = useLocation();
  usePageSeo(location.pathname);
  return (
    <>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          className="public-site"
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
        >
          <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
          <Routes location={location}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/book" element={<BookingPage />} />
        <Route path="/booking/success" element={<BookingSuccess />} />
        <Route path="/privatlivspolitik" element={<PrivacyPolicy />} />
        <Route path="/handelsbetingelser" element={<TermsPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/erhverv" element={<BusinessPage />} />
        {/* Fjernede flader (login, kundeområde, admin, Stripe-retur) */}
        <Route path="/mine-bestillinger" element={<ExternalRedirect to={PORTAL_URL} />} />
        <Route path="/booking/cancel" element={<BookingCancel />} />
        <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
        </motion.div>
      </AnimatePresence>
    </>
  );
}

function App() {
  useEffect(() => {
    // GA/Meta Pixel indlæses KUN efter samtykke i cookie-banneret.
    if (getCookieConsent() === "accepted") loadAnalytics();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <IntroAnimation />
      <BrowserRouter>
        <CookieBanner />
        <AppRoutes />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
